import { api, upload as uploadFile } from './api';

/**
 * التقاط الشاشة على مستوى النظام كلّه.
 *
 * منطق الالتقاط كان محبوسًا في كونسول المكالمات، فلا يراقَب الموظف إلا وهو
 * في مركز الاتصال. هنا صار مستقلًّا: يبدؤه الموظف من الشريط العلوي فيعمل في
 * أيّ شاشةٍ يفتحها، وكلُّ لقطةٍ تحمل اسم شاشتها لحظةَ أُخذت.
 *
 * ## حدٌّ يفرضه المتصفح
 *
 * لا تُلتقط شاشةٌ بلا إذنٍ صريح من صاحبها عبر نافذة المشاركة، ويبقى مؤشّرها
 * ظاهرًا أمامه طوال الجلسة. هذا أمانٌ في المتصفح لا نقصٌ في النظام — ولا
 * سبيل لتجاوزه، ولا ينبغي.
 */

export interface MonitorPolicy {
  mode: 'OFF' | 'SCREENSHOTS' | 'VIDEO';
  screenshotEverySec: number;
}

/** أين الموظف الآن — يُقرأ لحظةَ كل التقاطٍ فيرافق اللقطة اسمُ شاشتها. */
export type MetaGetter = () => { path: string; label: string };

const post = async (path: string, blob: Blob, name: string, meta: MetaGetter) => {
  const { path: p, label } = meta();
  const form = new FormData();
  form.append('file', new File([blob], name, { type: blob.type }));
  form.append('path', p);
  form.append('label', label);
  await uploadFile(path, form);
};

/**
 * يلتقط من مسار الشاشة حسب السياسة، ويعيد دالة إيقاف.
 *
 * لقطاتٌ: إطار على قماشٍ مصغَّر (١٤٤٠ عرضًا) يُرفع JPEG.
 * فيديو: مقاطع خمس دقائق مستقلة — فسقوطُ الشبكة يُسقط مقطعًا لا الجلسة كلها.
 */
export interface CaptureOptions {
  /**
   * تُسأل قبل كلّ لقطة: هل نلتقط الآن؟
   *
   * في تطبيق سطح المكتب تعود بـ«نعم» ما دامت نافذةُ GoyOne هي المُركَّز
   * عليها، فإن فتح الموظّفُ تطبيقًا آخر تُتخطّى اللقطة — لا تُصوَّر شاشتُه
   * خارج النظام. وفي المتصفّح لا تُمرَّر، فيلتقط كالمعتاد.
   */
  shouldCapture?: () => boolean;
}

export function startScreenCapture(
  display: MediaStream,
  policy: MonitorPolicy,
  meta: MetaGetter,
  onError: (msg: string) => void,
  opts: CaptureOptions = {},
): () => void {
  const track = display.getVideoTracks()[0];
  if (!track || policy.mode === 'OFF') return () => undefined;

  if (policy.mode === 'SCREENSHOTS') {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.srcObject = new MediaStream([track]);
    void video.play();

    let failed = 0;
    const timer = window.setInterval(() => {
      if (opts.shouldCapture && !opts.shouldCapture()) return;
      if (video.videoWidth === 0) return;
      const scale = Math.min(1, 1440 / video.videoWidth);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(video.videoWidth * scale);
      canvas.height = Math.round(video.videoHeight * scale);
      canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => {
          if (!blob) return;
          post('/callcenter/monitoring/shot', blob, 'shot.jpg', meta).catch(() => {
            /* ثلاث إخفاقات متتالية تستحق صوتًا — لا صمت يوهم أن المراقبة تعمل. */
            failed += 1;
            if (failed === 3) onError('تعذّر رفع لقطات المراقبة');
          });
        },
        'image/jpeg',
        0.7,
      );
    }, policy.screenshotEverySec * 1000);

    return () => {
      window.clearInterval(timer);
      video.srcObject = null;
    };
  }

  /* فيديو: مقاطع خمس دقائق مستقلة. */
  const SEGMENT_MS = 5 * 60_000;
  let rec: MediaRecorder | null = null;
  let stopped = false;

  /*
   * يوقف التسجيلَ مؤقّتًا حين يخرج الموظّفُ من النظام، ويستأنفه حين يعود —
   * فلا يُسجَّل ما يفعله خارج التطبيق. بلا الشرط يسجّل بلا انقطاع كالسابق.
   */
  const focusTimer = opts.shouldCapture
    ? window.setInterval(() => {
        if (!rec) return;
        const wanted = opts.shouldCapture!();
        if (!wanted && rec.state === 'recording') rec.pause();
        else if (wanted && rec.state === 'paused') rec.resume();
      }, 1000)
    : 0;

  const startSegment = () => {
    if (stopped || track.readyState !== 'live') return;
    const chunks: Blob[] = [];
    rec = new MediaRecorder(new MediaStream([track]), {
      mimeType: 'video/webm;codecs=vp8',
      videoBitsPerSecond: 500_000,
    });
    rec.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };
    rec.onstop = () => {
      const blob = new Blob(chunks, { type: 'video/webm' });
      if (blob.size > 0) {
        post('/callcenter/monitoring/segment', blob, 'shift.webm', meta).catch(() =>
          onError('تعذّر رفع مقطع الفيديو'),
        );
      }
      startSegment();
    };
    rec.start();
    window.setTimeout(() => {
      if (rec?.state === 'recording') rec.stop();
    }, SEGMENT_MS);
  };

  startSegment();

  return () => {
    stopped = true;
    if (focusTimer) window.clearInterval(focusTimer);
    if (rec && rec.state !== 'inactive') rec.stop();
  };
}

/**
 * المشاهدة الحيّة عند طلب المدير.
 *
 * يسأل الخادمَ كل ثلاث ثوانٍ: هل يشاهدني مدير؟ فإن نعم دفع إطارًا كل ثانيتين،
 * وإلا توقّف. لا بثٌّ دائم يُرهق الشبكة — الإطارات تجري لحظةَ يفتح مديرٌ شاشة
 * هذا الموظف فقط.
 */
export function startLiveWatch(display: MediaStream): () => void {
  const track = display.getVideoTracks()[0];
  if (!track) return () => undefined;

  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.srcObject = new MediaStream([track]);
  void video.play();

  let pushing = false;
  let pushTimer = 0;

  const pushFrame = () => {
    if (video.videoWidth === 0) return;
    const scale = Math.min(1, 1280 / video.videoWidth);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const form = new FormData();
        form.append('file', new File([blob], 'frame.jpg', { type: 'image/jpeg' }));
        void uploadFile('/callcenter/monitor/frame', form).catch(() => undefined);
      },
      'image/jpeg',
      0.6,
    );
  };

  const startPushing = () => {
    if (pushing) return;
    pushing = true;
    pushFrame();
    pushTimer = window.setInterval(pushFrame, 2000);
  };

  const stopPushing = () => {
    if (!pushing) return;
    pushing = false;
    window.clearInterval(pushTimer);
  };

  /* عبر `api` لا `fetch` مباشر: رمزُ الوصول في الترويسة، وبدونه 401 فلا يبدأ الدفع أبدًا. */
  const poll = window.setInterval(async () => {
    try {
      const { watched } = await api<{ watched: boolean }>('/callcenter/monitor/watched');
      if (watched) startPushing();
      else stopPushing();
    } catch {
      stopPushing();
    }
  }, 3000);

  return () => {
    window.clearInterval(poll);
    stopPushing();
    video.srcObject = null;
  };
}
